import { Injectable, Optional, Inject } from '@angular/core';
import { TOKENS as AUTH_TOKENS, IdentityService, Identity } from '@trustedshops/tswp-core-authorization';
import { toPromise } from '@trustedshops/tswp-core-common-eventing-rxjs';
import { TOKENS as PLUGIN_TOKENS, PluginRegistration, PluginRouter } from '@trustedshops/tswp-core-plugins';
import { TOKENS as UI_TOKENS, RouteSourceService } from '@trustedshops/tswp-core-ui';

/**
 * Service for managing user and route tracking in Instana.
 */
@Injectable()
export class InstanaTrackingService {
  constructor(
    @Optional()
    @Inject(AUTH_TOKENS.IdentityService)
    private readonly _identityService?: IdentityService,
    @Optional()
    @Inject(UI_TOKENS.RouteSourceService)
    private readonly _routeSourceService?: RouteSourceService,
    @Optional()
    @Inject(PLUGIN_TOKENS.PluginRouter)
    private readonly _pluginRouter?: PluginRouter
  ) {}

  /**
   * Sets up user tracking based on identity information.
   */
  async setupUserTracking(onError?: (error: Error) => void): Promise<void> {
    if (!this._identityService) {
      return;
    }

    try {
      interface TokenPayload {
        identityRef?: string;
        'https://etrusted.com'?: {
          accountRef?: string;
        };
      }

      const identity = await toPromise<Identity<any>>(
        this._identityService.identity
      );

      if (!identity) {
        return;
      }

      // Generic token payload extraction that works with different auth providers
      // Supports Keycloak structure: profile.keycloak.token.payload
      // Can be extended for other providers by checking different paths
      let payload: TokenPayload | null = null;

      // Try Keycloak structure first (for backward compatibility)
      if (identity.profile?.keycloak?.token?.payload) {
        payload = await toPromise<TokenPayload>(
          identity.profile.keycloak.token.payload
        );
      }
      // Future: Add support for other auth providers here
      // else if (identity.profile?.otherProvider?.token?.payload) { ... }

      if (!payload) {
        return;
      }

      const identityRef = payload.identityRef ?? 'unknown';
      const accountRef = payload['https://etrusted.com']?.accountRef ?? 'unknown';

      const ineum = (window as any).ineum;
      if (!ineum) {
        const error = new Error('Instana EUM API (ineum) not available.');
        if (onError) {
          onError(error);
        }
        return;
      }

      ineum('user', identityRef);
      ineum('meta', 'accountRef', accountRef);
    } catch (error) {
      const errorObj = error instanceof Error ? error : new Error(String(error));
      if (onError) {
        onError(errorObj);
      }
    }
  }

  /**
   * Sets up route tracking to monitor navigation changes.
   */
  setupRouteTracking(onError?: (error: Error) => void): void {
    if (!this._routeSourceService || !this._pluginRouter) {
      return;
    }

    this._routeSourceService.registerRouteWatcher((route) => {
      const cleanRoute = route.split('?')[0];
      const pluginRegistration = this._pluginRouter.getPluginRegistrationOfActiveRoute();

      if (!pluginRegistration) {
        this.setRouteInformation('unknown', 'unknown', cleanRoute, onError);
        return;
      }

      const version = this.determinePluginVersionFromPluginRegistration(pluginRegistration);
      this.setRouteInformation(pluginRegistration.name, version, cleanRoute, onError);
    });
  }

  private setRouteInformation(name: string, version: string, page: string, onError?: (error: Error) => void): void {
    const ineum = (window as any).ineum;
    if (!ineum) {
      const error = new Error('Instana EUM API (ineum) not available.');
      if (onError) {
        onError(error);
      }
      return;
    }
    ineum('meta', 'pluginName', name);
    ineum('meta', 'pluginVersion', version);
    ineum('page', page);
  }

  private determinePluginVersionFromPluginRegistration(
    pluginRegistration: PluginRegistration
  ): string {
    const basePath = pluginRegistration.initialRequest.basePath;
    const name = pluginRegistration.initialRequest.name;
    return basePath.replace(`${name}/`, '');
  }
}
